import { z } from 'zod';

/**
 * How a Runloop-proxied config presents its credential to the upstream, and
 * what extra headers it adds on top.
 *
 * Two reflex resources are proxied by Runloop and share this shape: AI Gateway
 * configs (`plugin-ai-gateway`) and remote MCP server configs (`plugin-mcp`).
 * Both hand Runloop a config plus a secret *reference* and let the platform
 * inject the value — reflex never builds an `Authorization` header itself — so
 * the contract here is deliberately about *placement*, not credentials.
 *
 * This module is the single source of truth for that contract. The Runloop
 * wire mappers and drift comparators live next to the SDK types in
 * `@reflex/plugin-api/services` (`runloop-proxy-auth.ts`); everything that is
 * persisted, validated, or rendered comes from here.
 *
 * @module
 */

/**
 * RFC 7230 `token` — the grammar for an HTTP field name. Notably excludes CR,
 * LF, `:`, and whitespace, so a name that parses can never smuggle a second
 * header into the proxied request.
 */
const HTTP_TOKEN = /^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/;

/** An HTTP header name. Grammar only — see {@link CustomHeaderNameSchema}. */
export const HttpHeaderNameSchema = z
  .string()
  .min(1, 'Header name is required')
  .max(128, 'Header name is too long')
  .regex(HTTP_TOKEN, "Use a valid HTTP header name (letters, digits, and !#$%&'*+-.^_`|~).");

/**
 * Header names a custom header may not take, lower-cased.
 *
 * Runloop applies custom headers *after* the auth mechanism, so a custom
 * `authorization` would silently overwrite the config's own credential — the
 * request would still succeed, against the wrong identity, with nothing in the
 * reflex UI to explain it. `host` re-points the request at a different origin
 * on servers that route by `Host`, which defeats the point of pinning
 * `endpoint`. Both are rejected at the boundary rather than left to Runloop.
 */
export const RESERVED_CUSTOM_HEADER_NAMES: readonly string[] = ['authorization', 'host'];

/** Runloop accepts at most this many custom headers per config. */
export const MAX_CUSTOM_HEADERS = 8;

/**
 * Upper bound on a custom header's value, literal or secret-backed. Well under
 * the ~8 KiB total header budget most servers enforce, and far above any real
 * API key, tenant id, or version pin. An unbounded field on a config that is
 * replayed into every proxied request is a footgun, not a feature.
 */
export const MAX_CUSTOM_HEADER_VALUE_LENGTH = 4096;

/**
 * RFC 9110 `field-value`, narrowed to US-ASCII: VCHAR plus SP and HTAB. Excludes
 * `obs-text`, which has no interoperable encoding in a header.
 */
const HTTP_FIELD_VALUE = /^[\t\x20-\x7e]*$/;

/**
 * A custom header's value, literal or secret-backed.
 *
 * Names are already narrowed to `[A-Za-z0-9_-]`, which makes header injection
 * *through a name* impossible — but the value is replayed verbatim into the
 * upstream request. Without this, `{ name: 'X-Tenant', value: 'acme\r\n
 * Authorization: Bearer …' }` would route around both the name grammar and
 * {@link RESERVED_CUSTOM_HEADER_NAMES} if Runloop's proxy does not validate on
 * write. Reflex parses at the boundary rather than assume it does.
 *
 * Empty is allowed: a header with an empty value is legal HTTP, and the secret
 * arm applies its own `min(1)` because a blank credential never is.
 */
export const CustomHeaderValueSchema = z
  .string()
  .max(MAX_CUSTOM_HEADER_VALUE_LENGTH, 'Header value is too long')
  .regex(
    HTTP_FIELD_VALUE,
    'Use printable ASCII only — line breaks and control characters cannot go in a header value.',
  );

/**
 * The header-name grammar shared by every reflex-managed header list.
 *
 * Deliberately narrower than {@link HttpHeaderNameSchema}: a secret-backed
 * header's name becomes a component of the secret name that stores its value,
 * and that name is interpolated into a URL path (`/v1/secrets/<name>`).
 * Restricting to `[A-Za-z0-9_-]` keeps that derivation injective and
 * URL-safe — otherwise `x.y` and `x_y` would sanitize to the same secret and
 * silently share a value. Every header name in real-world use is inside this
 * subset.
 */
export const HeaderTokenNameSchema = z
  .string()
  .min(1, 'Header name is required')
  .max(128, 'Header name is too long')
  .regex(/^[A-Za-z0-9_-]+$/, 'Use letters, digits, hyphens, and underscores.');

/**
 * An HTTP header name usable as a *custom* header on a proxied config:
 * {@link HeaderTokenNameSchema} minus {@link RESERVED_CUSTOM_HEADER_NAMES},
 * which the proxy sets itself.
 */
export const CustomHeaderNameSchema = HeaderTokenNameSchema.refine(
  (name) => !RESERVED_CUSTOM_HEADER_NAMES.includes(name.toLowerCase()),
  {
    message: `Reserved header name. ${RESERVED_CUSTOM_HEADER_NAMES.join(' and ')} are set by the config itself.`,
  },
);

/**
 * One persisted custom header, as stored and as returned by reads.
 *
 * Discriminated on `isSecret`, mirroring `plugin-mcp`'s `McpEnvVarSchema` so
 * both editors read the same way:
 *
 * - `isSecret: false` — a literal `value`, stored in Postgres and returned by
 *   every read. Fine for `anthropic-version: 2023-06-01`; **not** for
 *   credentials.
 * - `isSecret: true` — only `secretName`, the name of a secret in the
 *   organization's Runloop account. The value is written to Runloop once and
 *   never stored at rest in reflex or returned by any read, because nothing in
 *   reflex ever needs to read a custom header back.
 */
export const CustomHeaderSchema = z.discriminatedUnion('isSecret', [
  z.object({
    name: CustomHeaderNameSchema,
    isSecret: z.literal(false),
    value: CustomHeaderValueSchema,
  }),
  z.object({
    name: CustomHeaderNameSchema,
    isSecret: z.literal(true),
    /** Name of the backing secret in the org's Runloop account. */
    secretName: z.string().min(1),
  }),
]);
export type CustomHeader = z.infer<typeof CustomHeaderSchema>;

/**
 * One custom header on the write path.
 *
 * The secret arm carries the plaintext `secretValue`. It is **optional** on
 * purpose: reads never return a header's secret, so an edit form that
 * round-trips an untouched header has nothing to send back. Omitting it means
 * "keep whatever is already stored under this name"; a name with nothing
 * stored is rejected by the service rather than persisted as a dangling
 * reference.
 */
export const CustomHeaderInputSchema = z.discriminatedUnion('isSecret', [
  z.object({
    name: CustomHeaderNameSchema,
    isSecret: z.literal(false),
    value: CustomHeaderValueSchema,
  }),
  z.object({
    name: CustomHeaderNameSchema,
    isSecret: z.literal(true),
    secretValue: CustomHeaderValueSchema.min(1).optional(),
  }),
]);
export type CustomHeaderInput = z.infer<typeof CustomHeaderInputSchema>;

/**
 * Reject a list that repeats a header name. Names are compared case-insensitively
 * because HTTP field names are; two entries differing only in case would make
 * which value wins depend on Runloop's iteration order.
 */
export function withUniqueHeaderNames<S extends z.ZodType<Array<{ name: string }>>>(schema: S) {
  // Generic over the schema, not the element, so `z.input`/`z.output` survive
  // for consumers that need both sides (react-hook-form's zodResolver).
  // Blank names are excluded from the collision check: element validation
  // already reports them as required, and a nameless row is a missing-name
  // problem, not a duplicate — two untouched rows must not read as a clash.
  return schema.refine(
    (headers) => {
      const names = headers.map((h) => h.name.toLowerCase()).filter((name) => name.length > 0);
      return new Set(names).size === names.length;
    },
    { message: 'Header names must be unique (case-insensitive).' },
  );
}

/** The persisted custom-header list. */
export const CustomHeadersSchema = withUniqueHeaderNames(
  z.array(CustomHeaderSchema).max(MAX_CUSTOM_HEADERS, `At most ${MAX_CUSTOM_HEADERS} headers.`),
);

/** The custom-header list on the write path. */
export const CustomHeadersInputSchema = withUniqueHeaderNames(
  z
    .array(CustomHeaderInputSchema)
    .max(MAX_CUSTOM_HEADERS, `At most ${MAX_CUSTOM_HEADERS} headers.`),
);

/**
 * Where the proxy puts the config's own credential.
 *
 * - `bearer` — `Authorization: Bearer <secret>` (OpenAI, xAI, OpenRouter).
 * - `header` — sets `key` to the secret value, overwriting whatever the agent
 *   sent. Required by upstreams that authenticate with a non-bearer header
 *   (Anthropic's `x-api-key`, Google's `x-goog-api-key`).
 * - `basic` — `Authorization: Basic <base64>`. Store the secret as a plain
 *   `user:pass` pair; **Runloop does the base64 encoding**, so a pre-encoded
 *   value is encoded twice and fails to authenticate.
 *
 * Modeled as a discriminated union so a `header` mechanism always carries its
 * header name — Runloop's own `auth_mechanism` is `{ type: string; key?: string }`,
 * which cannot express that.
 */
export const ProxyAuthMechanismSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('bearer') }),
  z.object({ type: z.literal('header'), key: HttpHeaderNameSchema }),
  z.object({ type: z.literal('basic') }),
]);
export type ProxyAuthMechanism = z.infer<typeof ProxyAuthMechanismSchema>;

/** The mechanism applied when a config does not pin one. */
export const DEFAULT_PROXY_AUTH_MECHANISM: ProxyAuthMechanism = { type: 'bearer' };

/**
 * Describe a mechanism for UI copy and log context. Kept here so the label a
 * user sees and the value that is persisted cannot drift apart.
 */
export function proxyAuthMechanismLabel(auth: ProxyAuthMechanism): string {
  switch (auth.type) {
    case 'bearer':
      return 'Bearer token';
    case 'header':
      return `Custom header (${auth.key})`;
    case 'basic':
      return 'Basic (user:pass)';
  }
}

/**
 * Whether two mechanisms describe the same credential placement. Edit forms
 * use this to skip sending an unchanged mechanism, so a metadata-only save
 * never forces the Runloop round-trip the services reconcile on.
 */
export function proxyAuthMechanismsEqual(a: ProxyAuthMechanism, b: ProxyAuthMechanism): boolean {
  if (a.type !== b.type) return false;
  return a.type !== 'header' || b.type !== 'header' || a.key === b.key;
}

/**
 * Seed a header editor from the persisted list. Secret-backed entries come
 * back as a reference with no value (reads never return one), so they start
 * blank — leaving them blank on save keeps the stored credential.
 */
export function storedCustomHeadersToInput(stored: readonly CustomHeader[]): CustomHeaderInput[] {
  return stored.map((h) =>
    h.isSecret
      ? { name: h.name, isSecret: true as const, secretValue: undefined }
      : { name: h.name, isSecret: false as const, value: h.value },
  );
}

/**
 * Whether an edited header list is unchanged from what's persisted, so a
 * metadata-only save doesn't send `customHeaders` (Runloop replaces the list
 * wholesale, and sending it forces the reconcile round-trip). A re-entered
 * secret value always counts as a change — there is no stored value to
 * compare it against. Names compare case-insensitively and order-free,
 * matching how the list is validated and applied.
 */
export function customHeaderInputsMatchStored(
  input: readonly CustomHeaderInput[],
  stored: readonly CustomHeader[],
): boolean {
  if (input.length !== stored.length) return false;
  if (input.some((h) => h.isSecret && h.secretValue !== undefined)) return false;
  const storedByName = new Map(stored.map((h) => [h.name.toLowerCase(), h]));
  return input.every((h) => {
    const match = storedByName.get(h.name.toLowerCase());
    if (!match || match.isSecret !== h.isSecret) return false;
    return match.isSecret || (!h.isSecret && match.value === h.value);
  });
}
